/**
 * Scaled size helper for styled-components.
 *
 * Produces a CSS `calc()` expression that scales a pixel value by the
 * `--zoom` custom property. In Normal font mode `--zoom` is `1` (no change);
 * in Large font mode it is `1.25`, so every dimension grows proportionally.
 *
 * Special cases:
 * - `sz(0)` returns `"0px"` (no scaling needed).
 * - `sz(1)` and `sz(-1)` return `"1px"` / `"-1px"` (too small to scale
 *   meaningfully — keeps borders and nudges crisp).
 *
 * Use for component-specific dimensions (width, height, border-radius,
 * position offsets) — values that don't belong on the spacing token scale.
 *
 * For whitespace (margin, padding, gap) prefer the `--space-N` CSS variables
 * (1–30) which also scale with zoom but provide a constrained, discoverable
 * set of values.
 *
 * @example
 * ```ts
 * import { sz } from "@remhealth/ui";
 *
 * const Container = styled.div`
 *   width: ${sz(400)};
 *   height: ${sz(36)};
 *   border-radius: ${sz(8)};
 * `;
 * ```
 */
export function sz(px: number): string {
  // Too small to scale meaningfully — keeps borders and nudges crisp
  if (px === 0 || px === 1 || px === -1) {
    return `${px}px`;
  }
  return `calc(${px}px * var(--zoom, 1))`;
}
